import { CognitoUserPool, CognitoUser, AuthenticationDetails, CognitoUserAttribute } from 'amazon-cognito-identity-js';

// Cognito configuration
const poolData = {
    UserPoolId: process.env.REACT_APP_USER_POOL_ID,
    ClientId: process.env.REACT_APP_USER_POOL_CLIENT_ID,
};

const userPool = new CognitoUserPool(poolData);

export class CognitoService {
    // Sign up a new user
    static signUp(email, password, attributes = {}) {
        return new Promise((resolve, reject) => {
            const attributeList = [];
            
            // Add email attribute
            const dataEmail = {
                Name: 'email',
                Value: email,
            };
            const attributeEmail = new CognitoUserAttribute(dataEmail);
            attributeList.push(attributeEmail);

            // Add other attributes if provided
            Object.keys(attributes).forEach(key => {
                const attr = new CognitoUserAttribute({
                    Name: key,
                    Value: attributes[key],
                });
                attributeList.push(attr);
            });

            userPool.signUp(email, password, attributeList, null, (err, result) => {
                if (err) {
                    reject(err);
                    return;
                }
                resolve(result);
            });
        });
    }

    // Confirm sign up with verification code
    static confirmSignUp(email, confirmationCode) {
        return new Promise((resolve, reject) => {
            const cognitoUser = new CognitoUser({
                Username: email,
                Pool: userPool,
            });

            cognitoUser.confirmRegistration(confirmationCode, true, (err, result) => {
                if (err) {
                    reject(err);
                    return;
                }
                resolve(result);
            });
        });
    }

    // Sign in user
    static signIn(email, password) {
        return new Promise((resolve, reject) => {
            const authenticationDetails = new AuthenticationDetails({
                Username: email,
                Password: password,
            });

            const cognitoUser = new CognitoUser({
                Username: email,
                Pool: userPool,
            });

            cognitoUser.authenticateUser(authenticationDetails, {
                onSuccess: (result) => {
                    resolve({
                        user: cognitoUser,
                        session: result,
                        tokens: {
                            accessToken: result.getAccessToken().getJwtToken(),
                            idToken: result.getIdToken().getJwtToken(),
                            refreshToken: result.getRefreshToken().getToken(),
                        }
                    });
                },
                onFailure: (err) => {
                    reject(err);
                },
                newPasswordRequired: (userAttributes, requiredAttributes) => {
                    // Handle new password required scenario
                    resolve({
                        user: cognitoUser,
                        newPasswordRequired: true,
                        userAttributes,
                        requiredAttributes
                    });
                }
            });
        });
    }

    // Set new password (for new password required scenario)
    static setNewPassword(cognitoUser, newPassword, attributes = {}) {
        return new Promise((resolve, reject) => {
            cognitoUser.completeNewPasswordChallenge(newPassword, attributes, {
                onSuccess: (result) => {
                    resolve({
                        user: cognitoUser,
                        session: result,
                        tokens: {
                            accessToken: result.getAccessToken().getJwtToken(),
                            idToken: result.getIdToken().getJwtToken(),
                            refreshToken: result.getRefreshToken().getToken(),
                        }
                    });
                },
                onFailure: (err) => {
                    reject(err);
                }
            });
        });
    }

    // Sign out current user
    static signOut() {
        const cognitoUser = userPool.getCurrentUser();
        if (cognitoUser) {
            cognitoUser.signOut();
            // Clear local storage
            localStorage.clear();
            sessionStorage.clear();
        }
    }

    // Get current user
    static getCurrentUser() {
        return new Promise((resolve, reject) => {
            const cognitoUser = userPool.getCurrentUser();
            
            if (!cognitoUser) {
                reject(new Error('No user found'));
                return;
            }

            cognitoUser.getSession((err, session) => {
                if (err) {
                    reject(err);
                    return;
                }

                if (!session.isValid()) {
                    reject(new Error('Session is not valid'));
                    return;
                }

                // Get user attributes
                cognitoUser.getUserAttributes((err, attributes) => {
                    if (err) {
                        reject(err);
                        return;
                    }

                    const userAttributes = {};
                    attributes.forEach(attribute => {
                        userAttributes[attribute.getName()] = attribute.getValue();
                    });

                    resolve({
                        user: cognitoUser,
                        session,
                        attributes: userAttributes,
                        tokens: {
                            accessToken: session.getAccessToken().getJwtToken(),
                            idToken: session.getIdToken().getJwtToken(),
                            refreshToken: session.getRefreshToken().getToken(),
                        }
                    });
                });
            });
        });
    }

    // Check if user is authenticated
    static isAuthenticated() {
        return new Promise((resolve) => {
            this.getCurrentUser()
                .then(() => resolve(true))
                .catch(() => resolve(false));
        });
    }

    // Forgot password
    static forgotPassword(email) {
        return new Promise((resolve, reject) => {
            const cognitoUser = new CognitoUser({
                Username: email,
                Pool: userPool,
            });

            cognitoUser.forgotPassword({
                onSuccess: (result) => {
                    resolve(result);
                },
                onFailure: (err) => {
                    reject(err);
                }
            });
        });
    }

    // Confirm forgot password
    static confirmForgotPassword(email, confirmationCode, newPassword) {
        return new Promise((resolve, reject) => {
            const cognitoUser = new CognitoUser({
                Username: email,
                Pool: userPool,
            });

            cognitoUser.confirmPassword(confirmationCode, newPassword, {
                onSuccess: () => {
                    resolve('Password reset successful');
                },
                onFailure: (err) => {
                    reject(err);
                }
            });
        });
    }

    // Resend confirmation code
    static resendConfirmationCode(email) {
        return new Promise((resolve, reject) => {
            const cognitoUser = new CognitoUser({
                Username: email,
                Pool: userPool,
            });

            cognitoUser.resendConfirmationCode((err, result) => {
                if (err) {
                    reject(err);
                    return;
                }
                resolve(result);
            });
        });
    }
} 